import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Making a Node CLI both global and local",
  "description": "",
  "published": "2018-10-15T00:00:00.000Z",
  "redirect_from": ["/blog/making-node-cli-both-global-local/swizec/8665"],
  "categories": "Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Here's a fun engineering problem for ya: You want to build a CLI tool. It should run as a command anywhere in the terminal.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ do-the-thing
`}</code></pre>
    <p>{`And it does the thing. No `}<inlineCode parentName="p">{`./`}</inlineCode>{` or funny paths or anything like that. Just a nice command you can run from anywhere.`}</p>
    <p>{`That's not too hard.`}</p>
    <p>{`Now what if you want that CLI to be easily updateable? Still not hard. What if you want different projects to be able to run different versions of your CLI? To avoid issues with breaking changes in future versions.`}</p>
    <p><img parentName="p" {...{
        "src": "/42c4349b611a739a8317f17ae74b7587/media-l41Yi3OQTfDfMGLde-giphy.gif",
        "alt": null
      }}></img></p>
    <h2 {...{
      "id": "make-a-globally-executable-node-cli"
    }}>{`Make a globally executable Node CLI`}</h2>
    <p>{`Building executable CLIs with Node is pretty easy. Easy is a bad word in instructional writing, but it's true.`}</p>
    <p>{`Here's how you do it.`}</p>
    <p><strong parentName="p">{`1)`}</strong>{` You need an `}<inlineCode parentName="p">{`npm`}</inlineCode>{` project. Run `}<inlineCode parentName="p">{`npm init`}</inlineCode>{`, follow the prompts, do the things.`}</p>
    <p><strong parentName="p">{`2)`}</strong>{` Inside this project, create a `}<inlineCode parentName="p">{`cli.js`}</inlineCode>{` file. This will be your executable. No real code in here, just handling the execution and possibly argument parsing.`}</p>
    <pre><code parentName="pre" {...{}}>{`// ./cli.js

#!/usr/bin/env node

let command = require('./command')

command()
`}</code></pre>
    <p>{`The `}<inlineCode parentName="p">{`cli.js`}</inlineCode>{` file uses the hashbang syntax to tell your terminal to use `}<inlineCode parentName="p">{`node`}</inlineCode>{` to execute this file. You then import the adjacent `}<inlineCode parentName="p">{`command.js`}</inlineCode>{` file that exports some function. Run that function.`}</p>
    <p>{`You can now run this code with `}<inlineCode parentName="p">{`./cli.js`}</inlineCode>{` and it should run the command. Superb.`}</p>
    <p><strong parentName="p">{`3)`}</strong>{` To turn `}<inlineCode parentName="p">{`cli.js`}</inlineCode>{` into a command, you have to add a `}<inlineCode parentName="p">{`"bin"`}</inlineCode>{` directive to your `}<inlineCode parentName="p">{`package.json`}</inlineCode>{` file. Bin tells npm which commands should become global bash commands.`}</p>
    <pre><code parentName="pre" {...{}}>{`// package.json

{
    // ...
    "main": "command.js",
    "bin": {
        "do-the-thing": "cli.js"
    }
}
`}</code></pre>
    <p>{`You can list as many `}<inlineCode parentName="p">{`bin`}</inlineCode>{` commands as you'd like. Keys become bash commands while values are the Node files they map to.`}</p>
    <p>{`We set `}<inlineCode parentName="p">{`command.js`}</inlineCode>{` as the `}<inlineCode parentName="p">{`main`}</inlineCode>{` entry point into our module because that makes imports easier.`}</p>
    <p><strong parentName="p">{`4)`}</strong>{` Install globally.`}</p>
    <pre><code parentName="pre" {...{}}>{`$ npm install -g your-thing
`}</code></pre>
    <p>{`Installs your CLI in the global node_modules directory. Wherever that is on your machine.`}</p>
    <p>{`You can now run `}<inlineCode parentName="p">{`do-the-thing`}</inlineCode>{` from anywhere on your computer. The `}<inlineCode parentName="p">{`cli.js`}</inlineCode>{` file will execute, require its adjacent `}<inlineCode parentName="p">{`command.js`}</inlineCode>{`, and do the thing.`}</p>
    <p>{`Wonderful.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/10/global-execution.png",
        "alt": "A sketch of how execution works"
      }}></img></p>
    <h2 {...{
      "id": "make-an-optionally-local-globally-executable-node-cli"
    }}>{`Make an optionally local globally executable Node CLI`}</h2>
    <p>{`Here comes the fun part. How do we make it so that our global CLI runs different command files based on where you run the command?`}</p>
    <p>{`You want `}<inlineCode parentName="p">{`do-the-thing`}</inlineCode>{` to call `}<inlineCode parentName="p">{`cli.js`}</inlineCode>{`. But `}<inlineCode parentName="p">{`cli.js`}</inlineCode>{` should first try to run `}<inlineCode parentName="p">{`command.js`}</inlineCode>{` that's adjacent to where you're calling from. If that doesn't exist, it can run its own global version.`}</p>
    <p>{`Fortunately, Node has a great package resolution system that does just that. It climbs up the directory hierarchy looking for the nearest version of an import.`}</p>
    <p>{`Unfortunately, that doesn't work for us.`}</p>
    <p>{`We're executing the global `}<inlineCode parentName="p">{`cli.js`}</inlineCode>{` you see. That one is already at the top. Node never checks files local to where you're calling from :(`}</p>
    <p>{`There's a few ways we can hack around that. But only one I was able to make work.`}</p>
    <p>{`The hackiest.`}</p>
    <p>{`Something like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`// cli.js

let command = null;

try {
    command = require(\`\${process.cwd()}/node_modules/your-thing\`);
} catch (e) {
    command = require("your-thing");
}
`}</code></pre>
    <p>{`Start with an empty command. Then try to import the closest `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` to current working directory. If that fails, load whatever Node can find. Usually that's the global installation because it starts searching at `}<inlineCode parentName="p">{`cli.js`}</inlineCode>{`, which is global.`}</p>
    <p>{`Now you can run `}<inlineCode parentName="p">{`do-the-thing`}</inlineCode>{` from any project on your computer and each project can have its own version of your CLI. 👌`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/10/local-execution.png",
        "alt": "Sketch of how local execution works"
      }}></img></p>
    <p>{`One downside is that calling it from a subdirectory of your project will fail. Has to be at the root.`}</p>
    <p>{`It's a solvable problem. You can implement the same hierarchy traversal that Node uses.`}</p>
    <p>{`But that seems fishy 🤔`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      